<template>
  <div class="plan-content pricing-wrapper">
    <!-- Header section with title and action buttons (from Plan.vue) -->
    <div class="container-title">
      <div class="left">
        <h2 class="title">{{ $t('dash.plan.upgrade') }}</h2>
        <p>
          <router-link to="/dash/settings/billing">
            <a>{{ $t('dash.plan.subtitle') }}</a>
          </router-link>
          / {{ $t('dash.plan.upgrade') }}
        </p>
      </div>
      <div class="right">
        <router-link to="/dash/settings/billing">
          <div class="btn-outline-primary">{{ $t('dash.button.cancel') }}</div>
        </router-link>
        <div class="btn-primary" @click="beContacted">
          {{ $t('dash.plan.contact_me') }}
        </div>
      </div>
    </div>

    <!-- Main content: Configurator + Result Card (from pricing_webflow) -->
    <div class="content-plan">
      <div class="pricing-container">
        <!-- LEFT: Configurator (sliders) -->
        <section id="configurator" class="hide-scrollbar">
          <header class="header">
            <h1>{{ $t('dash.plan.simulation_title') }}</h1>
            <p class="subtitle">{{ $t('dash.plan.simulation_subtitle') }}</p>
          </header>

          <!-- Period selector (mensuel/1an/2ans) -->
          <PeriodSelector
            :selected-period="pricingState.period"
            @period-change="onPeriodChange"
          />

          <!-- Slider controls -->
          <div class="sliders-container">
            <ControlSlider
              v-for="control in sliderControls"
              :key="control.key"
              :label="control.label"
              :icon="control.icon"
              :value="pricingState[control.key]"
              :scale-name="control.scale"
              :unique-id="control.key"
              @input="onValueChange(control.key, $event)"
            />
          </div>
          <!-- Cancel subscription link (from Plan.vue) -->
          <p
            v-if="currentPlan"
            class="cancel-plan"
            @click.stop="cancelSubscriptionModal"
          >
            {{ $t('dash.plan.cancel_subscription') }}
          </p>
        </section>

        <!-- RIGHT: Result card -->
        <ResultCard
          :result="selectedPlan"
          :environment="environment"
          :is-current-plan="isCurrentPlan"
          @subscribe="startSubscription"
          @contact="beContacted"
        />
      </div>
    </div>

    <!-- Subscription loading overlay -->
    <SubscriptionLoader :show="loaders.subscription" />
  </div>
</template>

<script>
import { usePricingBasic } from '../pricing/composables/usePricingBasic'
// import { usePricingAdvanced } from '../pricing/composables/usePricingAdvanced' // For future switch
import ControlSlider from '../pricing/components/ControlSlider.vue'
import PeriodSelector from '../pricing/components/PeriodSelector.vue'
import ResultCard from '../pricing/components/ResultCard.vue'
import SubscriptionLoader from '../pricing/components/SubscriptionLoader.vue'
import {
  ENVIRONMENTS,
  INITIAL_VALUES,
  PERIODS,
  PRICING_MODES
} from '../pricing/config/pricingConfig'

// Import mixins from Plan.vue for Stripe logic
import PaymentMethodMixins from './../../mixins/PaymentMethod.vue'
import SubscriptionsMixins from './../../mixins/Subscriptions.vue'

import { EventBus } from '@/event-bus'

// Import icons
import networkIcon from '../pricing/assets/img/network-icon.svg'
import sourceIcon from '../pricing/assets/img/source-icon.svg'
import usersIcon from '../pricing/assets/img/users-icon.svg'

export default {
  name: 'Plan2',

  components: {
    PeriodSelector,
    ControlSlider,
    ResultCard,
    SubscriptionLoader
  },

  mixins: [SubscriptionsMixins, PaymentMethodMixins],

  props: {
    userProp: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      // Pricing mode (basic by default, can switch to advanced later)
      pricingMode: PRICING_MODES.BASIC,
      environment: ENVIRONMENTS.SAAS,

      // Pricing state
      pricingState: {
        period: PERIODS.ONE_YEAR, // Default: 1 year
        socialAccounts: 10,
        users: 5,
        sources: 10
      },

      // Slider controls configuration
      sliderControls: [
        {
          key: 'socialAccounts',
          label: this.$t('dash.plan.card.pages_number.title'),
          icon: networkIcon,
          scale: 'social'
        },
        {
          key: 'users',
          label: this.$t('dash.plan.card.users_number.title'),
          icon: usersIcon,
          scale: 'users'
        },
        {
          key: 'sources',
          label: this.$t('dash.plan.card.source_number.title'),
          icon: sourceIcon,
          scale: 'sources'
        }
      ],

      // Current user plan (for cancel subscription)
      currentPlan: null,
      user: {},
      loaders: {
        subscription: false
      }
    }
  },

  computed: {
    // Dynamic pricing calculator (currently using basic mode)
    // To switch to advanced mode: uncomment usePricingAdvanced import and use pricingMode condition
    pricingCalculator() {
      return usePricingBasic()
    },

    // Calculate selected plan based on current state
    selectedPlan() {
      return this.pricingCalculator.calculatePlan(this.pricingState)
    },

    // Check if selected plan is the user's current active plan
    isCurrentPlan() {
      if (!this.currentPlan || !this.user._premium || !this.user._premium.active) {
        return false
      }

      const planResult = this.selectedPlan
      if (!planResult || !planResult.plan) {
        return false
      }

      // Map plan ID to plan index
      const planNames = ['starter', 'standard', 'premium']
      const selectedPlanIndex = planNames.indexOf(planResult.plan.id) + 1

      // Check if plan matches
      if (selectedPlanIndex !== this.user._premium.plan) {
        return false
      }

      // Check if period matches
      const userPeriod = this.user._premium.is_2years
        ? PERIODS.TWO_YEARS
        : this.user._premium.is_annual
        ? PERIODS.ONE_YEAR
        : PERIODS.MONTHLY

      return planResult.period === userPeriod
    }
  },

  mounted() {
    // Event listeners from Plan.vue
    EventBus.$on('handlerCancelSubscription', this.cancelSubscription)
    EventBus.$on('handlerEditPaymentMethod', this.handlerEditPaymentMethod)
    EventBus.$on('handlerSubscribeFinish', this.handlerSubscribeFinish)
    EventBus.$on('handlerContactStaff', this.beContacted)
    EventBus.$on('handlerConfirmSubscription', this.handleConfirmSubscription)

    this.user = JSON.parse(localStorage.user)

    // Initialize plan from user subscription (if active)
    this.initPlan()

    // Set initial values ONLY if user has no active plan
    // This prevents overriding the values set by initPlan()
    if (!this.currentPlan) {
      const initialValues = INITIAL_VALUES[this.pricingMode]
      if (initialValues) {
        Object.keys(initialValues).forEach(key => {
          this.pricingState[key] = initialValues[key]
        })
      }
    }
  },

  beforeDestroy() {
    EventBus.$off('handlerCancelSubscription', this.cancelSubscription)
    EventBus.$off('handlerEditPaymentMethod', this.handlerEditPaymentMethod)
    EventBus.$off('handlerSubscribeFinish', this.handlerSubscribeFinish)
    EventBus.$off('handlerContactStaff', this.beContacted)
    EventBus.$off('handlerConfirmSubscription', this.handleConfirmSubscription)
  },

  methods: {
    /**
     * Initialize plan based on current user subscription (from Plan.vue)
     */
    initPlan(user) {
      user = user || JSON.parse(localStorage.user)

      if (
        user &&
        user._premium &&
        user._premium.active &&
        user._premium.plan > 0
      ) {
        this.currentPlan = user._premium.plan

        // Set period based on user subscription
        this.pricingState.period = user._premium.is_2years
          ? PERIODS.TWO_YEARS
          : user._premium.is_annual
          ? PERIODS.ONE_YEAR
          : PERIODS.MONTHLY

        // Set sliders to match user's current plan
        const planDefaults = {
          1: { socialAccounts: 10, users: 5, sources: 10 }, // Starter
          2: { socialAccounts: 60, users: 50, sources: 60 }, // Standard
          3: { socialAccounts: 250, users: 200, sources: 250 }, // Premium
          4: { socialAccounts: 5, users: 1, sources: 5 } // Freelance
        }

        const currentPlanDefaults = planDefaults[user._premium.plan]
        if (currentPlanDefaults) {
          this.pricingState.socialAccounts = currentPlanDefaults.socialAccounts
          this.pricingState.users = currentPlanDefaults.users
          this.pricingState.sources = currentPlanDefaults.sources
        }
      }
    },

    /**
     * Handle period change (mensuel/1an/2ans)
     */
    onPeriodChange(period) {
      this.pricingState.period = period
    },

    /**
     * Handle slider value change
     */
    onValueChange(key, value) {
      this.pricingState[key] = value
    },

    /**
     * Start subscription process (Stripe logic from Plan.vue)
     */
    startSubscription() {
      const user = JSON.parse(localStorage.user)
      const planResult = this.selectedPlan

      // If custom plan, redirect to contact
      if (planResult.plan.id === 'custom') {
        this.beContacted()
        return
      }

      const planNames = ['starter', 'standard', 'premium']
      const planIndex = planNames.indexOf(planResult.plan.id) + 1

      // Check if user has no active subscription
      if (!user._premium.active || user._premium.plan === 0) {
        // Check payment method before allowing subscription
        if (!this.hasRegisteredCard) {
          EventBus.$emit(
            'showModal',
            this.$t('dash.modal.information'),
            this.$t('dash.plan.subscription.payment_required'),
            this.$t('dash.button.add'),
            this.$t('dash.button.close'),
            'handlerEditPaymentMethod'
          )
          return
        }

        // Show confirmation modal
        this.showSubscriptionConfirmation(planIndex, planResult)
        return
      }

      // Check if DOWNGRADE attempt
      if (user._premium.plan > planIndex) {
        this.contactForDowngrade(user._premium.plan, planIndex, planResult)
        return
      }

      // Check if changing period: Annual/2years -> Monthly (NOT ALLOWED)
      if (
        (user._premium.is_annual || user._premium.is_2years) &&
        planResult.period === PERIODS.MONTHLY
      ) {
        EventBus.$emit(
          'showModal',
          this.$t('dash.modal.information'),
          this.$t('dash.plan.subscription.period_change_not_allowed'),
          this.$t('dash.button.close')
        )
        return
      }

      // Check if changing period: Monthly -> Annual/2years (CONTACT REQUIRED)
      if (
        !user._premium.is_annual &&
        !user._premium.is_2years &&
        (planResult.period === PERIODS.ONE_YEAR ||
          planResult.period === PERIODS.TWO_YEARS)
      ) {
        this.contactForPeriodChange(user._premium.plan, planIndex, planResult)
        return
      }

      // Check payment method
      if (!this.hasRegisteredCard) {
        EventBus.$emit(
          'showModal',
          this.$t('dash.modal.information'),
          this.$t('dash.plan.subscription.invalid_payment_method'),
          this.$t('dash.button.modify'),
          this.$t('dash.button.close'),
          'handlerEditPaymentMethod'
        )
        return
      }

      // UPGRADE is allowed - show confirmation modal
      this.showSubscriptionConfirmation(planIndex, planResult)
    },

    /**
     * Show subscription confirmation modal
     */
    showSubscriptionConfirmation(planIndex, planResult) {
      const periodLabels = {
        [PERIODS.MONTHLY]: this.$t('dash.plan.subscription.period_monthly'),
        [PERIODS.ONE_YEAR]: this.$t('dash.plan.subscription.period_annual'),
        [PERIODS.TWO_YEARS]: this.$t('dash.plan.subscription.period_biennial')
      }

      // Get plan name from planResult
      const planName =
        planResult.plan.name ||
        this.$t('dash.plan.subscription.custom_plan_name')
      const period =
        periodLabels[planResult.period] ||
        this.$t('dash.plan.subscription.custom_period')
      const price = (
        planResult.finalPrice ||
        planResult.totalPrice ||
        0
      ).toLocaleString(this.$i18n.locale)

      const confirmMessage = this.$t('dash.plan.subscription.confirm_message')
        .replace('{plan}', planName)
        .replace('{price}', price)
        .replace('{period}', period)

      // Store planIndex for the confirmation handler
      this._pendingPlanIndex = planIndex

      EventBus.$emit(
        'showModal',
        this.$t('dash.plan.subscription.confirm_title'),
        confirmMessage,
        this.$t('dash.confirm'),
        this.$t('dash.button.cancel'),
        'handlerConfirmSubscription'
      )
    },

    /**
     * Contact support for downgrade request
     */
    contactForDowngrade(currentPlan, newPlan, planResult) {
      const planNames = {
        1: 'Starter',
        2: 'Standard',
        3: 'Premium',
        4: 'Freelance'
      }
      window.$crisp.push(['do', 'chat:open'])

      const message = this.$t('dash.plan.subscription.downgrade_request')
        .replace('{currentPlan}', planNames[currentPlan])
        .replace('{newPlan}', planNames[newPlan])

      window.$crisp.push(['do', 'message:send', ['text', message]])
    },

    /**
     * Contact support for period change request
     */
    contactForPeriodChange(currentPlan, newPlan, planResult) {
      const planNames = {
        1: 'Starter',
        2: 'Standard',
        3: 'Premium',
        4: 'Freelance'
      }
      const periodLabels = {
        [PERIODS.MONTHLY]: this.$t('dash.plan.subscription.period_monthly'),
        [PERIODS.ONE_YEAR]: this.$t('dash.plan.subscription.period_annual'),
        [PERIODS.TWO_YEARS]: this.$t('dash.plan.subscription.period_biennial')
      }

      window.$crisp.push(['do', 'chat:open'])

      const message = this.$t('dash.plan.subscription.period_change_request')
        .replace('{currentPlan}', planNames[currentPlan])
        .replace('{period}', periodLabels[planResult.period])

      window.$crisp.push(['do', 'message:send', ['text', message]])
    },

    /**
     * Subscribe to plan with Stripe
     */
    async subscribeToPlan(planIndex) {
      const isAnnual = this.pricingState.period !== PERIODS.MONTHLY
      const is2Years = this.pricingState.period === PERIODS.TWO_YEARS
      const is2026Plan = [1, 2, 3].includes(planIndex)
      const options = is2026Plan ? [] : []

      const subscriptionPayload = {
        type: planIndex,
        is_annual: isAnnual,
        is_2years: is2Years,
        is_2026_plan: is2026Plan,
        options: options
      }

      this.loaders.subscription = true

      try {
        const res = await this.subscribePlan(subscriptionPayload)
        const { client_secret, success } = res.body
        this.loaders.subscription = false

        if (client_secret && success) {
          EventBus.$emit(
            'showModal',
            this.$t('dash.modal.information'),
            this.$t('dash.plan.subscription.update_payment_method'),
            this.$t('dash.button.modify'),
            this.$t('dash.button.close'),
            'handlerEditPaymentMethod'
          )
          return
        }

        if (success) {
          EventBus.$emit(
            'showModal',
            this.$t('dash.modal.information'),
            this.$t('dash.plan.subscription.success'),
            this.$t('dash.button.close')
          )

          const user = JSON.parse(localStorage.user)
          user._premium.plan = planIndex
          user._premium.active = true
          user._premium.is_annual = isAnnual
          localStorage.setItem('user', JSON.stringify(user))

          this.initPlan(user)
          this.user = user
        } else {
          EventBus.$emit(
            'showModal',
            this.$t('dash.modal.information'),
            this.$t('dash.plan.subscription.failed'),
            this.$t('dash.button.close')
          )
        }
      } catch (error) {
        this.loaders.subscription = false

        // Check if error is related to payment method
        const errorMessage = error.body && error.body.message
        const isPaymentError =
          errorMessage &&
          (errorMessage.includes('payment') ||
            errorMessage.includes('card') ||
            errorMessage.includes('paiement') ||
            errorMessage.includes('carte'))

        if (isPaymentError) {
          EventBus.$emit(
            'showModal',
            this.$t('dash.plan.subscription.payment_error_title'),
            this.$t('dash.plan.subscription.payment_error_message'),
            this.$t('dash.button.modify'),
            this.$t('dash.button.close'),
            'handlerEditPaymentMethod'
          )
        } else {
          EventBus.$emit(
            'showModal',
            this.$t('dash.plan.subscription.error_title'),
            errorMessage || this.$t('dash.plan.subscription.failed'),
            this.$t('dash.button.close')
          )
        }
      }
    },

    /**
     * Handle subscription confirmation
     */
    handleConfirmSubscription() {
      if (this._pendingPlanIndex) {
        this.subscribeToPlan(this._pendingPlanIndex)
        this._pendingPlanIndex = null
      }
    },

    /**
     * Handle subscription finish (from Plan.vue)
     */
    async handlerSubscribeFinish() {
      const planResult = this.selectedPlan
      const planNames = ['starter', 'standard', 'premium']
      const planIndex = planNames.indexOf(planResult.plan.id) + 1

      await this.subscribeToPlan(planIndex)
    },

    /**
     * Open cancel subscription modal
     */
    cancelSubscriptionModal() {
      EventBus.$emit(
        'showModal',
        this.$t('dash.modal.are_you_sur'),
        this.$t('dash.plan.cancel_subscription_modal'),
        this.$t('dash.confirm'),
        this.$t('dash.button.cancel'),
        'handlerCancelSubscription'
      )
    },

    /**
     * Cancel subscription (from Plan.vue)
     */
    cancelSubscription() {
      this.loaders.subscription = true
      this.subscribePlan({ plan: 0 }).then(res => {
        if (res) {
          const user = JSON.parse(localStorage.user)
          user._premium.plan = 0
          localStorage.setItem('user', JSON.stringify(user))
          this.user = user
          this.initPlan(user)
        }
        this.loaders.subscription = false
      })
    },

    /**
     * Redirect to payment method edit
     */
    handlerEditPaymentMethod() {
      this.$router.push('/dash/settings/billing')
    },

    /**
     * Open Crisp chat to contact support (from Plan.vue)
     */
    beContacted() {
      window.$crisp.push(['do', 'chat:open'])
      const planResult = this.selectedPlan

      if (planResult.plan.id === 'custom') {
        window.$crisp.push([
          'do',
          'message:send',
          ['text', this.$t('dash.plan.subscription.contact_custom')]
        ])
      } else {
        window.$crisp.push([
          'do',
          'message:send',
          [
            'text',
            `${this.$t('dash.plan.message_contact_me.main')} ${
              planResult.plan.name
            }`
          ]
        ])
      }

      window.$crisp.push([
        'do',
        'message:show',
        [
          'text',
          this.$t('dash.plan.message_contact_me.response').replace(
            '%s',
            this.userProp && this.userProp.firstname
              ? this.userProp.firstname
              : ''
          )
        ]
      ])
    }
  }
}
</script>

<style>
/* All pricing styles are centralized in /assets/scss/components/pricing.scss */
</style>
